import { makeStyles, Box, Divider, Grid, Typography, Paper } from '@material-ui/core'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import MyTextfield from '../../../control/textfield/textfield'
import MyDatePicker from '../../../control/datepicker/datepicker'
import MyTimePicker from '../../../control/timepicker/timepicker'
import { AccessTimeOutlined } from '@material-ui/icons'
import { useFormState } from 'react-final-form'
import useTranslate from '../../../i18n/useTranslate'
import TooltipControl from '../../../control/tooltip/tooltip'
import pdfDocumentIcon from '../../../images/pdf_document.svg'
import pdfDocumentWithPenIcon from '../../../images/pdf_document_with_pen.svg'
import docxFileIcon from '../../../images/docx_file.svg'
import pdfFileIcon from '../../../images/pdf_file.svg'

type fileVisibilityType = {
    showMemo: boolean
    showMemoSigned: boolean
    showWord: boolean
    showPDF: boolean
}

export type AgendaForm = {
    round: string
    createdDate: string
    title: string
    meetingDate: string
    startTime: string
    openTime: string
    location: string
    meetingLink: string
    details: string
}

type Props = {
    showExportButton?: boolean
    canEdit?: boolean
    onExportWord?: (form: AgendaForm) => void
    onExportPDF?: (form: AgendaForm) => void
    onExportMemo?: () => void
    onExportMemoSigned?: () => void
    fileVisibility?: fileVisibilityType
}

const AgendaDetailForm = ({
    showExportButton = false,
    canEdit = true,
    onExportWord,
    onExportPDF,
    onExportMemo,
    onExportMemoSigned,
    fileVisibility = {
        showMemo: false,
        showMemoSigned: false,
        showWord: false,
        showPDF: false,
    },
}: Props) => {
    const t = useTranslate()
    const classes = useStyles()
    const { values } = useFormState<{ [k: string]: any }>({
        subscription: { values: true, invalid: true, submitting: true },
    })

    const readAgenda = (): AgendaForm => {
        const v = values || {}
        return {
            round: v.round || '',
            createdDate: v.createdDate || '',
            title: v.title || '',
            meetingDate: v.meetingDate || '',
            startTime: v.startTime || '',
            openTime: v.openTime || '',
            location: v.location || '',
            meetingLink: v.meetingLink || '',
            details: v.details || '',
        }
    }

    return (
        <Paper className={classes.root} elevation={0}>
            <Box className={classes.headerBar}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {t.get('UM.SETTING_AGENDA.AGENDA.HEADER')}
                </Typography>
                {showExportButton && (
                    <Box className={classes.actionRow}>
                        {fileVisibility.showMemo && (
                            <TooltipControl title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_MEMO')} placement="bottom">
                                <div style={{ display: 'flex' }} onClick={onExportMemo}>
                                    <img
                                        src={pdfDocumentIcon}
                                        alt="pdf-document-icon"
                                        style={{ height: 40, cursor: 'pointer' }}
                                    />
                                </div>
                            </TooltipControl>
                        )}
                        {fileVisibility.showMemoSigned && (
                            <TooltipControl
                                title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_MEMO_SIGNED')}
                                placement="bottom"
                            >
                                <div style={{ display: 'flex' }} onClick={onExportMemoSigned}>
                                    <img
                                        src={pdfDocumentWithPenIcon}
                                        alt="pdf-document-with-pen-icon"
                                        style={{ height: 40, cursor: 'pointer' }}
                                    />
                                </div>
                            </TooltipControl>
                        )}
                        {fileVisibility.showWord && (
                            <TooltipControl title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_WORD')} placement="bottom">
                                <div style={{ display: 'flex' }} onClick={() => onExportWord?.(readAgenda())}>
                                    <img
                                        src={docxFileIcon}
                                        alt="docx-file-icon"
                                        style={{ height: 40, cursor: 'pointer' }}
                                    />
                                </div>
                            </TooltipControl>
                        )}
                        {fileVisibility.showPDF && (
                            <TooltipControl title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_PDF')} placement="bottom">
                                <div style={{ display: 'flex' }} onClick={() => onExportPDF?.(readAgenda())}>
                                    <img
                                        src={pdfFileIcon}
                                        alt="pdf-file-icon"
                                        style={{ height: 40, cursor: 'pointer' }}
                                    />
                                </div>
                            </TooltipControl>
                        )}
                    </Box>
                )}
            </Box>

            <Divider />

            <Box className={classes.section}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <MyTextfield
                            name="agendaNo"
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA.NO'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA.NO'),
                                disabled: !canEdit,
                            }}
                        />
                    </Grid>
                    {/* <Grid item xs={12} sm={6}>
                        <MyDatePicker
                            name="createdAt"
                            label={t.get('UM.SETTING_AGENDA.AGENDA.CREATED_AT')}
                            size="small"
                            disabled={true}
                            icon={<CalendarTodayIcon color="disabled" />}
                        />
                    </Grid> */}
                    <Grid item xs={12} sm={6}>
                        <MyTextfield
                            name="mailId"
                            required
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA.MAIL_ID'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA.MAIL_ID'),
                                disabled: !canEdit,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MyTextfield
                            name="agendaName"
                            required
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA.MEETING_NAME'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA.MEETING_NAME'),
                                disabled: !canEdit,
                                multiline: true,
                                rows: 2,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MyDatePicker
                            name="meetingDate"
                            label={t.get('UM.SETTING_AGENDA.AGENDA.MEETING_DATE')}
                            size="small"
                            disabled={!canEdit}
                            required
                            icon={<CalendarTodayIcon color="disabled" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MyTimePicker
                            name="meetingStartTime"
                            label={t.get('UM.SETTING_AGENDA.AGENDA.MEETING_START')}
                            disabled={!canEdit}
                            required
                            icon={<AccessTimeOutlined color="disabled" />}
                            minuteStep={1}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MyTimePicker
                            name="meetingEndTime"
                            label={t.get('UM.SETTING_AGENDA.AGENDA.MEETING_END')}
                            disabled={!canEdit}
                            required
                            icon={<AccessTimeOutlined color="disabled" />}
                            minuteStep={1}
                            range={{ start: values?.meetingStartTime || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MyTextfield
                            name="meetingLocation"
                            required
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA.LOCATION'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA.LOCATION'),
                                disabled: !canEdit,
                            }}
                        />
                    </Grid>
                    {/* <Grid item xs={12}>
                        <MyTextfield
                            name="meetingUrl"
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA.MEETING_URL'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA.MEETING_URL'),
                                disabled: !canEdit,
                            }}
                        />
                    </Grid> */}
                    {/* <Grid item xs={12}>
                        <MyTextfield
                            name="agendaDescription"
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA.DESCRIPTION'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA.DESCRIPTION'),
                                disabled: !canEdit,
                                multiline: true,
                                rows: 3,
                            }}
                        />
                    </Grid> */}
                </Grid>
            </Box>
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    root: { borderRadius: 12, border: '1px solid #E5E7EB', background: '#fff' },
    headerBar: {
        padding: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        fontWeight: 700,
    },
    actionRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
    section: { padding: theme.spacing(3) },
}))

export default AgendaDetailForm
